import { PROJECT_FORM_TYPES, AUX_MUTATION } from "../projectFormStore";
import { PROJECT_FORM_CONTROLLER_TYPES } from "../projectFormControllerStore";
import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { NO_DATA_DECIMAL } from "Forms/Components/ProjectForm/ProjectElements/projectElement";
import { idGenerator } from "Util/uniqueIdGenerator";
import {
    getValueOrDefault,
    getForMultiValue,
    getForStringMultiValue,
    getEquipmentItemMultiValue,
    sendMultiDataChange,
    mapMultiValues
} from "./projectElement.js";
import { ClientLocation } from "Util/clientLocation";

export const PV_SYSTEM_INPUT_BOUNDS = {
    MinTilt: 0,
    MaxTilt: 90,
    MinAzimuth: 0,
    MaxAzimuth: 359,
    MinMonthlyShading: 0,
    MaxMonthlyShading: 100,
    MinAzimuthalShading: 0,
    MaxAzimuthalShading: 90
};

const MonthlyShading = "monthly";
const SHADING_SEPARATOR = ",";
export const MONTHLY_OUTPUT_SEPARATOR = ";";
const TrackingDefault = "Fixed";

const inputFields = {
    InverterQuantity: "Inverter Quantity",
    InverterManufacturer: "Inverter Manufacturer",
    InverterModel: "Inverter Model",
    PVArrayDescription: "PV Array Description",
    PVModuleQuantity: "PV Module Quantity",
    PVModuleManufacturer: "PV Module Manufacturer",
    PVModuleModel: "PV Module Model",
    PVModuleAssignedInverter: "PV Module Assigned Inverter",
    Tilt: "PV Module Tilt",
    Azimuth: "PV Module Azimuth",
    Tracking: "PV Module Tracking",
    AzimuthalShading: "PV Module Azimuthal Shading",
    MonthlyShading: "PV Module Monthly Shading",
    EquipmentItemAssignedInverter: "Battery Assigned Inverter",
    EquipmentItemQuantity: "Battery Quantity",
    EquipmentItemManufacturer: "Battery Manufacturer",
    EquipmentItemModel: "Battery Model",
    EquipmentItemEquipmentType: "Battery Equipment Type",
    EquipmentItemEnergyRating: "Battery Energy Rating",
    EquipmentItemTechnology: "Battery Technology",
    ESSSubsystemType: "Subsystem Type"
};
const outputFields = {
    NameplateRating: "Nameplate Rating",
    CECRating: "CEC Rating",
    EstimatedProduction: "Estimated Production",
    DesignFactor: "Design Factor",
    InverterNameplateRating: "Inverter Nameplate Rating",
    PVConfiguration: "PV Configuration",
    AverageInverterEfficiency: "Average Inverter Efficiency",
    PTCModuleRating: "PTC Module Rating",
    MonthlyEstimatedProduction: "Monthly Estimated Production",
    HasAllSmartInverters: "Has All Smart Inverters",
    SystemInverterStandardValue: "System Inverter Standard Value",
    CSIRating: "CSI Rating",
    TotalNameplateEnergyCapacity: "Total Nameplate Energy Capacity"
};

export const SUFFIXES = {
    inputFields,
    outputFields
};

const pvIdGenerator = new idGenerator();

function getShadingForMultiValue(value) {
    value = getForMultiValue(value);
    const number = Number(value);
    if (isNaN(number)) {
        return value;
    }
    return number.toFixed(1);
}
function getInverterMultiValue(subsystems, propertyName) {
    return subsystems.map((s) => getForMultiValue(s[propertyName]));
}
function getPvModuleMultiValue(subsystems, propertyName) {
    return subsystems
        .map((s) => s.moduleGroups.map((m) => getForMultiValue(m[propertyName])))
        .reduce((p, c) => p.concat(c));
}
function getPvArrayDescriptionMultiValue(subsystems, propertyName) {
    return subsystems
        .map((s) => s.moduleGroups.map((m) => getForStringMultiValue(m[propertyName])))
        .reduce((p, c) => p.concat(c));
}
function getPvModuleShadingMultiValue(subsystems) {
    //prettier-ignore
    return subsystems
        .map(s => s.moduleGroups.map(
            m =>
                m.shading
                    .map(sh => getShadingForMultiValue(sh))
                    .join(SHADING_SEPARATOR)))
        .reduce((p, c) => p.concat(c));
}
function getPvModuleAssignedInverterMultiValue(subsystems) {
    //prettier-ignore
    return subsystems
        .map((s, index) => s.moduleGroups.map(() => index))
        .reduce((p, c) => p.concat(c));
}

function getEquipmentItemAssignedInverterMultiValue(subsystems) {
    //prettier-ignore
    return subsystems
        .map((s, index) => s.equipmentGroups.map(() => index))
        .reduce((p, c) => p.concat(c));
}

function isMonthlyShading(element) {
    return element.Properties.shadingmodel === MonthlyShading;
}

function computeSubsystems(element, getters) {
    const projectElement = getters[PROJECT_FORM_TYPES.getters.projectElementFromId](element.PublicId);
    const enabledDescriptions = getters[PROGRAM_TYPES.getters.EnablePVArrayDescription];
    const subsystemIds = projectElement.auxState.subsystemIds;
    const pvModuleIds = projectElement.auxState.pvModuleIds;
    const equipmentItemIds = projectElement.auxState.equipmentItemIds;
    const inputMap = mapMultiValues(element, inputFields, getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId]);

    const subsystems = inputMap.InverterQuantity.map((quantity, index) => {
        return {
            id: subsystemIds[index],
            quantity: getValueOrDefault(quantity),
            manufacturerId: getValueOrDefault(inputMap.InverterManufacturer[index]),
            modelId: getValueOrDefault(inputMap.InverterModel[index]),
            moduleGroups: [],
            equipmentGroups: [],
            index
        };
    });
    const shading = isMonthlyShading(element) ? inputMap.MonthlyShading : inputMap.AzimuthalShading;

    inputMap.PVModuleQuantity.forEach((quantity, index) => {
        // Make sure the assignedInverter value is valid before attempting to define the subsytem
        const assignedInverter = parseInt(inputMap.PVModuleAssignedInverter[index]);
        if (0 <= assignedInverter && assignedInverter < subsystems.length) {
            const shadingValues = shading[index]
                ? shading[index].split(SHADING_SEPARATOR).map((shadingValue) => getValueOrDefault(shadingValue))
                : getDefaultShading(element);
            const pvModule = {
                id: pvModuleIds[index],
                assignedInverter: assignedInverter,
                description: enabledDescriptions ? getValueOrDefault(inputMap.PVArrayDescription[index]) : null,
                quantity: getValueOrDefault(inputMap.PVModuleQuantity[index]),
                manufacturerId: getValueOrDefault(inputMap.PVModuleManufacturer[index]),
                modelId: getValueOrDefault(inputMap.PVModuleModel[index]),
                tilt: getValueOrDefault(inputMap.Tilt[index]),
                azimuth: getValueOrDefault(inputMap.Azimuth[index]),
                tracking: getValueOrDefault(inputMap.Tracking[index], TrackingDefault),
                shading: shadingValues,
                index
            };
            subsystems[assignedInverter].moduleGroups.push(pvModule);
        }
    }, this);

    if (inputMap.EquipmentItemQuantity) {
        inputMap.EquipmentItemQuantity.forEach((quantity, index) => {
            const assignedInverter = parseInt(inputMap.EquipmentItemAssignedInverter[index]);
            if (0 <= assignedInverter && assignedInverter < subsystems.length) {
                const equipmentItem = {
                    id: equipmentItemIds[index],
                    assignedInverter: assignedInverter,
                    quantity: getValueOrDefault(inputMap.EquipmentItemQuantity[index]),
                    manufacturerId: getValueOrDefault(inputMap.EquipmentItemManufacturer[index]),
                    modelId: getValueOrDefault(inputMap.EquipmentItemModel[index]),
                    energyRating: getValueOrDefault(inputMap.EquipmentItemEnergyRating[index]),
                    equipmentTechnology: getValueOrDefault(inputMap.EquipmentItemTechnology[index]),
                    equipmentType: getValueOrDefault(inputMap.EquipmentItemEquipmentType[index]),
                    index
                };
                subsystems[assignedInverter].equipmentGroups.push(equipmentItem);
            }
        }, this);
    }

    return subsystems;
}

function commitPvConfig(element, subsystems, systemIsNew, commit, dispatch) {
    const subsystemIds = getInverterMultiValue(subsystems, "id");
    const inverterQuantities = getInverterMultiValue(subsystems, "quantity");
    const inverterManufacturerIds = getInverterMultiValue(subsystems, "manufacturerId");
    const inverterModelIds = getInverterMultiValue(subsystems, "modelId");

    const pvArrayDescription = getPvArrayDescriptionMultiValue(subsystems, "description");
    const pvModuleIds = getPvModuleMultiValue(subsystems, "id");
    const pvModuleAssignedInverter = getPvModuleAssignedInverterMultiValue(subsystems);
    const pvModuleQuantities = getPvModuleMultiValue(subsystems, "quantity");
    const pvModuleManufacturerIds = getPvModuleMultiValue(subsystems, "manufacturerId");
    const pvModuleModelIds = getPvModuleMultiValue(subsystems, "modelId");
    const tilt = getPvModuleMultiValue(subsystems, "tilt");
    const azimuth = getPvModuleMultiValue(subsystems, "azimuth");
    const tracking = getPvModuleMultiValue(subsystems, "tracking");
    const shading = getPvModuleShadingMultiValue(subsystems);

    const equipmentItemIds = getEquipmentItemMultiValue(subsystems, "id");
    const equipmentItemAssignedInverter = getEquipmentItemAssignedInverterMultiValue(subsystems);
    const equipmentItemQuantities = getEquipmentItemMultiValue(subsystems, "quantity");
    const equipmentItemManufacturerIds = getEquipmentItemMultiValue(subsystems, "manufacturerId");
    const equipmentItemModelIds = getEquipmentItemMultiValue(subsystems, "modelId");
    const equipmentItemEquipmentTypes = getEquipmentItemMultiValue(subsystems, "equipmentType");
    const equipmentItemEnergyRatings = getEquipmentItemMultiValue(subsystems, "energyRating");
    const equipmentItemTechnologies = getEquipmentItemMultiValue(subsystems, "equipmentTechnology");

    const shadingField = isMonthlyShading(element) ? inputFields.MonthlyShading : inputFields.AzimuthalShading;

    commit(
        AUX_MUTATION,
        {
            elementId: element.PublicId,
            auxState: {
                subsystemIds,
                pvModuleIds,
                equipmentItemIds,
                isDirty: !systemIsNew
            }
        },
        { root: true }
    );

    sendMultiDataChange(element, inputFields.InverterQuantity, inverterQuantities, dispatch);
    sendMultiDataChange(element, inputFields.InverterManufacturer, inverterManufacturerIds, dispatch);
    sendMultiDataChange(element, inputFields.InverterModel, inverterModelIds, dispatch);

    sendMultiDataChange(element, inputFields.PVArrayDescription, pvArrayDescription, dispatch);
    sendMultiDataChange(element, inputFields.PVModuleAssignedInverter, pvModuleAssignedInverter, dispatch);
    sendMultiDataChange(element, inputFields.PVModuleQuantity, pvModuleQuantities, dispatch);
    sendMultiDataChange(element, inputFields.PVModuleManufacturer, pvModuleManufacturerIds, dispatch);
    sendMultiDataChange(element, inputFields.PVModuleModel, pvModuleModelIds, dispatch);
    sendMultiDataChange(element, inputFields.Tilt, tilt, dispatch);
    sendMultiDataChange(element, inputFields.Azimuth, azimuth, dispatch);
    sendMultiDataChange(element, inputFields.Tracking, tracking, dispatch);
    sendMultiDataChange(element, shadingField, shading, dispatch);

    sendMultiDataChange(element, inputFields.EquipmentItemAssignedInverter, equipmentItemAssignedInverter, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemQuantity, equipmentItemQuantities, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemManufacturer, equipmentItemManufacturerIds, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemModel, equipmentItemModelIds, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemEquipmentType, equipmentItemEquipmentTypes, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemEnergyRating, equipmentItemEnergyRatings, dispatch);
    sendMultiDataChange(element, inputFields.EquipmentItemTechnology, equipmentItemTechnologies, dispatch);

    clearOutputValues(element, dispatch);
}

function clearOutputValues(element, dispatch) {
    const promises = [];
    for (const key in outputFields) {
        if (outputFields.hasOwnProperty(key)) {
            const suffix = outputFields[key];
            const dataFieldId = element.GroupDataFields[suffix];
            if (suffix && dataFieldId) {
                promises.push(
                    dispatch(
                        PROJECT_FORM_TYPES.actions.setProjectData,
                        {
                            dataFieldId,
                            value: null
                        },
                        { root: true }
                    )
                );
            }
        }
    }
    return Promise.all(promises);
}
function getDefaultShading(element) {
    const shadingCount = isMonthlyShading(element) ? 12 : 7;
    return new Array(shadingCount).fill("");
}
function getEmptyModule(element) {
    return {
        id: pvIdGenerator.getNextId("pvSystemPvModule"),
        quantity: "",
        manufacturerId: "",
        modelId: "",
        tilt: "",
        azimuth: "",
        tracking: TrackingDefault,
        shading: getDefaultShading(element)
    };
}
function getEmptyEquipmentItem(element, equipmentType) {
    return {
        id: pvIdGenerator.getNextId("pvSystemEquipmentItem"),
        equipmentType: equipmentType,
        energyRating: NO_DATA_DECIMAL,
        equipmentTechnology: "",
        quantity: "",
        manufacturerId: "",
        modelId: ""
    };
}
function getEmptySubsystem(element) {
    const pvModule = getEmptyModule(element);
    pvModule.assignedInverter = 0;
    return {
        id: pvIdGenerator.getNextId("pvSystemSubsystem"),
        quantity: "",
        manufacturerId: "",
        modelId: "",
        moduleGroups: [pvModule],
        // Do not include a Battery equipment item in the default Empty Subsystem
        equipmentGroups: []
    };
}

function getAuxProjectElementState(element, projectElement, Project) {
    const dataFieldMultiValueFromId = (dataFieldId) => Project.DataFields[dataFieldId].MultiInstanceData;
    const inputMap = mapMultiValues(element, inputFields, dataFieldMultiValueFromId);
    const subsystemIds = inputMap.InverterQuantity.map(() => pvIdGenerator.getNextId("pvSystemSubsystem"));
    const pvModuleIds = inputMap.PVModuleQuantity.map(() => pvIdGenerator.getNextId("pvSystemPvModule"));
    let equipmentItemIds = null;
    if (inputMap.EquipmentItemQuantity) {
        equipmentItemIds = inputMap.EquipmentItemQuantity.map(() => pvIdGenerator.getNextId("pvSystemEquipmentItem"));
    }

    return {
        subsystemIds,
        pvModuleIds,
        equipmentItemIds
    };
}

async function onDependencyUpdate({ $app, element, dataFieldId, rootGetters, dispatch }) {
    // PV systems only have one dependency, the location.
    // If that changes and we already have a calculated value, we need to show a dialog to prompt for recalc

    // Respond to the location update only if the PV System is "complete" -- determined by looking at the
    //  Estimated Production field
    if (isComplete(element, rootGetters)) {
        const dependencyDataField = rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](dataFieldId);
        const locationIsLatLon = dependencyDataField.TypeId === DATA_FIELD_TYPE.Location;
        const locationValue = rootGetters[PROJECT_TYPES.getters.dataFieldValueFromId](dataFieldId);

        // If the location input is not given or is invalid, the output values will be cleared
        let clearOutput = false;
        if (locationIsLatLon) {
            // Make sure lat-lon location is valid
            const clientLocation = ClientLocation.fromServerString(locationValue);
            clearOutput = !clientLocation.isValid;
        } else {
            // Clear instead of recalculating if zip code is being erased
            clearOutput = (locationValue || "") === "";
        }

        const okResult = "OK";
        const cancelResult = "CANCEL";
        const dialogResult = await $app.showDialog({
            title: "Confirm Changes",
            message: clearOutput
                ? "Changing this field will cause the calculated values for your PV System to be cleared. Continue?"
                : "Changing this field will cause the calculated values for your PV System to be recalculated. Continue?",
            buttons: [
                {
                    text: "Cancel",
                    result: cancelResult,
                    testRole: "ChangeZipCancel"
                },
                {
                    text: "OK",
                    primary: true,
                    result: okResult,
                    testRole: "ChangeZipOK"
                }
            ],
            testRole: "ChangeZipDialog"
        });
        if (dialogResult === cancelResult) {
            throw new Error("PV System zip change cancelled.");
        }

        if (clearOutput) {
            await clearOutputValues(element, dispatch);
        }

        await dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, null, { root: true });

        if (!clearOutput) {
            const result = await dispatch(
                PROJECT_FORM_TYPES.actions.calcPvOutput,
                { elementId: element.PublicId },
                { root: true }
            );
            if (result.hasError) {
                await $app.showErrorDialog("Calculation failed", result.error);
                await clearOutputValues(element, dispatch);
            }
        }
    }
}

function isComplete(element, getters) {
    //get estimated production
    const productionId = element.GroupDataFields[outputFields.EstimatedProduction];
    const value = getters[PROJECT_TYPES.getters.dataFieldValueFromId](productionId);

    return value && value > 0;
}

export const pvSystemController = {
    isMonthlyShading,
    computeSubsystems,
    commitPvConfig,
    clearOutputValues,
    getEmptyModule,
    getEmptyEquipmentItem,
    getEmptySubsystem,
    getAuxProjectElementState,
    onDependencyUpdate,
    isComplete
};
